/**
 * Constants for income tax calculator
 */

import { 
  Lock, 
  Landmark, 
  Heart, 
  Share2, 
  BarChart2, 
  CircleDollarSign,
  User,
  Briefcase
} from "lucide-react";
import { CalculatorValues } from "./types";

// Non-resident tax brackets (ATO)
// Source: https://www.ato.gov.au/tax-rates-and-codes/tax-rates-foreign-residents
export const NON_RESIDENT_TAX_BRACKETS = {
  "2024-2025": [
    { min: 0, max: 45000, rate: 0.325, base: 0 },
    { min: 45001, max: 135000, rate: 0.30, base: 14625 },
    { min: 135001, max: 190000, rate: 0.37, base: 41625 },
    { min: 190001, max: Infinity, rate: 0.45, base: 61975 },
  ],
  "2023-2024": [
    { min: 0, max: 120000, rate: 0.325, base: 0 },
    { min: 120001, max: 180000, rate: 0.37, base: 39000 },
    { min: 180001, max: Infinity, rate: 0.45, base: 61200 },
  ],
  "2022-2023": [
    { min: 0, max: 120000, rate: 0.325, base: 0 },
    { min: 120001, max: 180000, rate: 0.37, base: 39000 },
    { min: 180001, max: Infinity, rate: 0.45, base: 61200 },
  ],
  "2021-2022": [
    { min: 0, max: 120000, rate: 0.325, base: 0 },
    { min: 120001, max: 180000, rate: 0.37, base: 39000 },
    { min: 180001, max: Infinity, rate: 0.45, base: 61200 },
  ],
  "2020-2021": [
    { min: 0, max: 120000, rate: 0.325, base: 0 },
    { min: 120001, max: 180000, rate: 0.37, base: 39000 },
    { min: 180001, max: Infinity, rate: 0.45, base: 61200 },
  ]
};

// Working Holiday Maker tax brackets (ATO)
// Source: https://www.ato.gov.au/tax-rates-and-codes/tax-rates-working-holiday-makers
export const WORKING_HOLIDAY_TAX_BRACKETS = {
  "2024-2025": [
    { min: 0, max: 45000, rate: 0.15, base: 0 },
    { min: 45001, max: 135000, rate: 0.30, base: 6750 },
    { min: 135001, max: 190000, rate: 0.37, base: 33750 },
    { min: 190001, max: Infinity, rate: 0.45, base: 54100 },
  ],
  "2023-2024": [
    { min: 0, max: 45000, rate: 0.15, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 6750 },
    { min: 120001, max: 180000, rate: 0.37, base: 31125 },
    { min: 180001, max: Infinity, rate: 0.45, base: 53325 },
  ],
  "2022-2023": [
    { min: 0, max: 45000, rate: 0.15, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 6750 },
    { min: 120001, max: 180000, rate: 0.37, base: 31125 },
    { min: 180001, max: Infinity, rate: 0.45, base: 53325 },
  ],
  "2021-2022": [
    { min: 0, max: 45000, rate: 0.15, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 6750 },
    { min: 120001, max: 180000, rate: 0.37, base: 31125 },
    { min: 180001, max: Infinity, rate: 0.45, base: 53325 },
  ],
  "2020-2021": [
    { min: 0, max: 45000, rate: 0.15, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 6750 },
    { min: 120001, max: 180000, rate: 0.37, base: 31125 },
    { min: 180001, max: Infinity, rate: 0.45, base: 53325 },
  ]
};

// Resident tax brackets for different financial years (ATO)
export const TAX_BRACKETS_BY_YEAR = {
  "2024-2025": [
    { min: 0, max: 18200, rate: 0, base: 0 },
    { min: 18201, max: 45000, rate: 0.16, base: 0 },
    { min: 45001, max: 135000, rate: 0.30, base: 4288 },
    { min: 135001, max: 190000, rate: 0.37, base: 31288 },
    { min: 190001, max: Infinity, rate: 0.45, base: 51638 },
  ],
  "2023-2024": [
    { min: 0, max: 18200, rate: 0, base: 0 },
    { min: 18201, max: 45000, rate: 0.19, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 5092 },
    { min: 120001, max: 180000, rate: 0.37, base: 29467 },
    { min: 180001, max: Infinity, rate: 0.45, base: 51667 },
  ],
  "2022-2023": [
    { min: 0, max: 18200, rate: 0, base: 0 },
    { min: 18201, max: 45000, rate: 0.19, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 5092 },
    { min: 120001, max: 180000, rate: 0.37, base: 29467 },
    { min: 180001, max: Infinity, rate: 0.45, base: 51667 },
  ],
  "2021-2022": [
    { min: 0, max: 18200, rate: 0, base: 0 },
    { min: 18201, max: 45000, rate: 0.19, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 5092 },
    { min: 120001, max: 180000, rate: 0.37, base: 29467 },
    { min: 180001, max: Infinity, rate: 0.45, base: 51667 },
  ],
  "2020-2021": [
    { min: 0, max: 18200, rate: 0, base: 0 },
    { min: 18201, max: 45000, rate: 0.19, base: 0 },
    { min: 45001, max: 120000, rate: 0.325, base: 5092 },
    { min: 120001, max: 180000, rate: 0.37, base: 29467 },
    { min: 180001, max: Infinity, rate: 0.45, base: 51667 },
  ],
};

// Default to current tax year brackets
export const TAX_BRACKETS = TAX_BRACKETS_BY_YEAR["2024-2025"];

// Medicare Levy Rate (2% of taxable income)
export const MEDICARE_LEVY_RATE = 0.02;

// Medicare Levy Surcharge Tiers (for individuals without private health cover)
export const MEDICARE_LEVY_SURCHARGE = [
  { min: 0, max: 93000, rate: 0 },
  { min: 93001, max: 108000, rate: 0.01 },
  { min: 108001, max: 144000, rate: 0.0125 },
  { min: 144001, max: Infinity, rate: 0.015 },
];

// Division 293 tax threshold (additional 15% tax on super contributions for high income earners)
export const DIVISION_293_THRESHOLD = 250000;

// Concessional contributions cap (limit on super contributions that receive concessional tax treatment)
export const CONCESSIONAL_CONTRIBUTIONS_CAP = 30000;

// Effective rate of Division 293 tax (additional 15%)
export const DIVISION_293_EFFECTIVE_RATE = 0.15;

// Superannuation Guarantee Rates by year
export const SUPER_RATES_BY_YEAR = {
  "2024-2025": 0.115,
  "2023-2024": 0.11,
  "2022-2023": 0.105,
  "2021-2022": 0.10,
  "2020-2021": 0.095,
};

// Default to current super rate
export const SUPER_GUARANTEE_RATE = SUPER_RATES_BY_YEAR["2024-2025"];

// HELP/HECS Repayment Thresholds and Rates (2023-24)
export const HELP_REPAYMENT_THRESHOLDS = [
  { min: 0, max: 51550, rate: 0 },
  { min: 51551, max: 57154, rate: 0.01 },
  { min: 57155, max: 62764, rate: 0.02 },
  { min: 62765, max: 68140, rate: 0.025 },
  { min: 68141, max: 73513, rate: 0.03 },
  { min: 73514, max: 79212, rate: 0.035 },
  { min: 79213, max: 84911, rate: 0.04 },
  { min: 84912, max: 90936, rate: 0.045 },
  { min: 90937, max: 96963, rate: 0.05 },
  { min: 96964, max: 102661, rate: 0.055 },
  { min: 102662, max: 110351, rate: 0.06 },
  { min: 110352, max: 115881, rate: 0.065 },
  { min: 115882, max: 121411, rate: 0.07 },
  { min: 121412, max: 126941, rate: 0.075 },
  { min: 126942, max: 132470, rate: 0.08 },
  { min: 132471, max: 138000, rate: 0.085 },
  { min: 138001, max: 143529, rate: 0.09 },
  { min: 143530, max: 150788, rate: 0.095 },
  { min: 150789, max: Infinity, rate: 0.1 },
];

// Default calculator values
export const DEFAULT_CALCULATOR_VALUES: CalculatorValues = {
  income: 85000,
  bonusIncome: 0,
  employmentType: "employee",
  frequency: "annually",
  incomeInputType: "annually",
  taxYear: "2024-2025",
  hoursPerWeek: 38,
  isResident: true,
  claimTaxFreeThreshold: true,
  hasHELP: false,
  hasPrivateHealth: true,
  superRate: SUPER_GUARANTEE_RATE,
  deductions: 0,
  showAdvanced: false,
  // Medicare fields
  medicareExempt: "none",
  // Super fields
  salarySacrifice: 0,
  salarySacrificeFrequency: "annually",
  salaryIncludesSuper: false,
  additionalSuper: 0,
  voluntarySuper: 0,
  carryForwardSuper: 0,
  // Tax offset fields
  incomeLevies: 0,
  taxOffsets: 0,
  dependantChildren: 0,
  hasSpouseRebate: false,
  spouseRebate: 0,
  seniorsPensionerOffset: false,
};

// Payment Frequency Options
export const PAYMENT_FREQUENCIES = [
  { value: "annually", label: "Annually" },
  { value: "monthly", label: "Monthly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "weekly", label: "Weekly" },
];

// Income Input Type Options
export const INCOME_INPUT_TYPES = [
  { value: "hourly", label: "Hourly Rate" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
];

// Tax Year Options
export const TAX_YEARS = [
  { value: "2024-2025", label: "2024-2025 (Stage 3 Tax Cuts)" },
  { value: "2023-2024", label: "2023-2024" },
  { value: "2022-2023", label: "2022-2023" },
  { value: "2021-2022", label: "2021-2022" },
  { value: "2020-2021", label: "2020-2021" },
];

// Employment Type Options
export const EMPLOYMENT_TYPES = [
  { value: "employee", label: "Employee", icon: User },
  { value: "abn", label: "ABN Holder", icon: Briefcase },
];

// Tax settings FAQ tooltips
export const TAX_SETTINGS_TOOLTIPS = {
  resident: "Tax residency affects your tax rates and threshold. Most people living in Australia are residents for tax purposes.",
  taxFreeThreshold: "The first $18,200 of your income is tax-free. Only claim this for your main job.",
  helpDebt: "Student loan repayments are calculated based on your income and are automatically withheld from your pay.",
  privateHealth: "Affects Medicare Levy Surcharge which applies to higher income earners without private hospital cover.",
  medicareExempt: "Some individuals may be exempt from the Medicare Levy due to specific circumstances such as low income or eligibility for certain health services.",
  // Super tooltips
  salarySacrifice: "Reduce your taxable income by contributing to your super fund before tax is applied. This can lower your tax and boost your retirement savings.",
  salaryIncludesSuper: "If your salary package includes superannuation, your take-home pay will be lower as the super contribution is part of your gross salary rather than being additional.",
  additionalSuper: "Voluntary contributions to your super fund above the mandatory employer contribution. These are made after-tax but may be tax-deductible.",
  voluntarySuper: "Personal super contributions that you may be able to claim as a tax deduction. Limited by the concessional contributions cap of $30,000 per year.",
  carryForwardSuper: "You can carry forward any unused concessional super contribution cap from the previous five years, as long as your total super balance is less than $500,000.",
  // Tax offset tooltips
  incomeLevies: "Additional taxes that may apply based on specific circumstances or income levels, such as the Temporary Budget Repair Levy.",
  taxOffsets: "Tax offsets (also known as rebates) directly reduce the amount of tax you pay. Different offsets apply to different personal circumstances.",
  dependantChildren: "You may be eligible for tax offsets if you maintain a dependent child. This information is used to calculate any applicable family tax benefits.",
  spouseRebate: "If your spouse's income is below certain thresholds, you may be eligible for a tax offset. Enable the option and enter your spouse's annual income to calculate the rebate automatically.",
  seniorsPensionerOffset: "The seniors and pensioners tax offset (SAPTO) can reduce the amount of tax you pay if you're eligible. You must meet certain age, income and eligibility requirements."
};

// FAQ items
export const FAQ_ITEMS = [
  {
    question: "How accurate is this tax calculator?",
    answer: "MyTaxPal uses official ATO tax rates and formulas for the current and upcoming financial years including the 2024-25 Stage 3 tax cuts. While highly accurate for standard situations, complex tax affairs may require professional advice."
  },
  {
    question: "What's the difference between ABN and employee taxation?",
    answer: "Employees have tax withheld by their employer, while ABN holders (contractors) are responsible for paying their own tax. ABN holders may claim business expenses but don't receive paid leave or superannuation. They also need to charge and remit GST if earning over $75,000 annually."
  },
  {
    question: "Does MyTaxPal save my financial information?",
    answer: "No. MyTaxPal processes all calculations directly in your browser. We don't store, track, or share any of your financial information. Your data remains completely private."
  },
  {
    question: "How is superannuation calculated?",
    answer: "For employees, employers must contribute a minimum percentage of your ordinary time earnings to your superannuation fund (11% for 2023-24, increasing to 11.5% for 2024-25). This is calculated on your pre-tax income and is paid in addition to your salary unless your package is structured as 'inclusive of super'."
  }
];

// Features info cards
export const FEATURES_INFO = [
  {
    icon: Lock,
    title: "Secure & Private",
    description: "All income tax calculations happen locally in your browser. We never store or share your financial or personal data."
  },
  {
    icon: Landmark,
    title: "ATO-Compliant Tax Calculator",
    description: "Our take-home pay calculator uses the latest 2024–25 tax brackets and rules from the Australian Taxation Office (ATO)."
  },
  {
    icon: Heart,
    title: "100% Free, No Sign-Up",
    description: "MyTaxPal is a free Australian tax calculator—no login, no sign-up. Just accurate and simple tax help for everyone."
  },
  {
    icon: Share2,
    title: "Share Your Results",
    description: "Easily share your tax estimate and net income breakdown with friends, family, or your accountant."
  },
  {
    icon: BarChart2,
    title: "Compare Tax Scenarios",
    description: "Compare different salaries, ABN vs PAYG, and super contributions to see how your choices affect take-home pay."
  },
  {
    icon: CircleDollarSign,
    title: "Smart Saving & Tax Tips",
    description: "Get helpful financial tips to boost your savings, reduce tax, and plan smarter for your next financial year."
  },
];

// Tax offset constants
export const DEPENDANT_CHILDREN_OFFSET = {
  BASE_AMOUNT: 3000, // Base amount per dependant child
  ADDITIONAL_AMOUNT: 1000, // Additional amount for each child after the first
  MAX_INCOME: 120000, // Maximum income threshold for the offset
  PHASE_OUT_RATE: 0.04 // 4% reduction for each dollar over the threshold
};

export const SPOUSE_REBATE = {
  BASE_AMOUNT: 2880, // Maximum rebate amount
  THRESHOLD: 15000, // Income threshold for spouse
  PHASE_OUT_RATE: 0.25, // 25% reduction for each dollar over the threshold
  MAX_INCOME: 40000 // Maximum income for spouse to be eligible
};

export const SENIORS_PENSIONER_OFFSET = {
  SINGLE: {
    BASE_AMOUNT: 2230,
    THRESHOLD: 32279,
    PHASE_OUT_RATE: 0.125 // 12.5% reduction for each dollar over the threshold
  },
  COUPLE: {
    BASE_AMOUNT: 1602,
    THRESHOLD: 28974,
    PHASE_OUT_RATE: 0.125
  },
  ILLNESS_SEPARATED: {
    BASE_AMOUNT: 2230,
    THRESHOLD: 32279,
    PHASE_OUT_RATE: 0.125
  }
};
